import { useUIMessages } from "@convex-dev/agent/react";
import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useMemo } from "react";
import {
  compareClaudiaTranscriptMessages,
  readClaudiaTranscriptStreamMetadata,
  resolveClaudiaTranscriptMessageThreadId,
} from "../lib/claudia-transcript-identity";
import type { AssistantTranscriptMessageRuntime } from "../types";

function normalizeTranscriptMessage(
  message: AssistantTranscriptMessageRuntime
): AssistantTranscriptMessageRuntime {
  const parts = Array.isArray(message.parts) ? message.parts : [];
  const text =
    typeof message.text === "string"
      ? message.text
      : parts
          .filter((part): part is { type: "text"; text: string } => part.type === "text")
          .map((part) => part.text)
          .join("");
  const streamMetadata = readClaudiaTranscriptStreamMetadata(message);

  return {
    ...message,
    threadId: resolveClaudiaTranscriptMessageThreadId(message),
    sourceOrder:
      typeof message.sourceOrder === "number"
        ? message.sourceOrder
        : typeof streamMetadata?.sourceOrder === "number"
          ? streamMetadata.sourceOrder
          : message.order,
    promptMessageId:
      typeof message.promptMessageId !== "undefined"
        ? message.promptMessageId
        : (streamMetadata?.promptMessageId ?? null),
    promptExecutionAttemptId:
      typeof message.promptExecutionAttemptId !== "undefined"
        ? message.promptExecutionAttemptId
        : (streamMetadata?.promptExecutionAttemptId ?? null),
    promptAttemptNumber:
      typeof message.promptAttemptNumber !== "undefined"
        ? message.promptAttemptNumber
        : (streamMetadata?.promptAttemptNumber ?? null),
    promptAttemptPhase:
      typeof message.promptAttemptPhase !== "undefined"
        ? message.promptAttemptPhase
        : (streamMetadata?.promptAttemptPhase ?? null),
    sourceRunId:
      typeof message.sourceRunId !== "undefined"
        ? message.sourceRunId
        : (streamMetadata?.sourceRunId ?? null),
    messageKind:
      typeof message.messageKind !== "undefined"
        ? message.messageKind
        : (streamMetadata?.messageKind ?? undefined),
    transcriptItemKey:
      typeof message.transcriptItemKey === "string"
        ? message.transcriptItemKey
        : typeof streamMetadata?.transcriptItemKey === "string"
          ? streamMetadata.transcriptItemKey
          : undefined,
    finality:
      message.finality === "streaming" || message.finality === "final"
        ? message.finality
        : streamMetadata?.finality === "streaming" || streamMetadata?.finality === "final"
          ? streamMetadata.finality
          : undefined,
    text,
    parts,
  };
}

/**
 * A row belongs to the thread on screen only if it says so (LAT-950).
 *
 * This used to admit rows with no thread at all, as a transient allowance for live stream rows.
 * Those rows do name their thread — through `metadata.claudiaTranscript.threadId` — while the
 * backend's settled rows carried no thread whatsoever, so the allowance quietly admitted every
 * row of every thread the vendored cache still held. The compensating filter it needed (drop
 * anything whose id was on screen before the last switch) then hid the real rows of a thread the
 * user came back to, and never released them, because it only cleared once something rendered.
 */
function belongsToThread(message: AssistantTranscriptMessageRuntime, threadId: string | null) {
  if (!threadId) {
    return false;
  }
  return message.threadId === threadId;
}

const claudiaTranscriptQuery = api.claudia.listTranscriptMessages as unknown;

export function useClaudiaTranscriptMessages(args: {
  projectId: Id<"projects">;
  threadId: string | null;
  initialNumItems?: number;
}) {
  const queryArgs = args.threadId
    ? ({ projectId: args.projectId, threadId: args.threadId } as unknown as {
        projectId: Id<"projects">;
      })
    : "skip";
  const query = (
    useUIMessages as unknown as (...params: unknown[]) => {
      results?: unknown[];
      loadMore: (numItems?: number) => void;
      status?: string;
      [key: string]: unknown;
    }
  )(claudiaTranscriptQuery, queryArgs, {
    initialNumItems: args.initialNumItems ?? 20,
    stream: true,
  });
  const results = useMemo(
    () =>
      args.threadId && Array.isArray(query?.results)
        ? (query.results as AssistantTranscriptMessageRuntime[])
            .map(normalizeTranscriptMessage)
            .filter((message) => belongsToThread(message, args.threadId))
            .sort(compareClaudiaTranscriptMessages)
        : [],
    [args.threadId, query?.results]
  );

  return {
    ...query,
    results,
  };
}
